import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import Checkbox from '@components/fields/Checkbox';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RichTextArea from '@components/fields/RichTextArea';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import useMyExpert from '@hooks/useMyExpert';
import {
  GetJourneyWithDocumentsQuery,
  WorksheetFragment as Worksheet,
  WorksheetUpdateDocument,
  WorksheetUpdateMutation,
  WorksheetUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { sortByLabel } from '@utils/modelUtils';

type FormData = {
  completed: boolean;
  description: string;
  folderId: string | null;
  title: string;
};

type Props = {
  autofocus: boolean;
  journey: GetJourneyWithDocumentsQuery['journey'];
  onCancel: () => void;
  onSuccess: () => void;
  worksheet: Worksheet;
};

const WorksheetForm = ({ autofocus, journey, onCancel, onSuccess, worksheet }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const defaultValues: FormData = {
    completed: worksheet.completed,
    description: worksheet.description,
    folderId: worksheet.fileResource?.folder?.id ?? null,
    title: worksheet.title
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateWorksheet] = useMutation<WorksheetUpdateMutation, WorksheetUpdateMutationVariables>(
    WorksheetUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      completed: yup.boolean().defined(),
      description: yup.string().required(intl.formatMessage({ id: 'errors.worksheet.description.blank' })),
      folderId: yup.string().nullable().defined(),
      title: yup.string().required(intl.formatMessage({ id: 'errors.worksheet.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const completed = form.watch('completed');

  const folderOptions = useMemo(() => {
    const folders = sortByLabel(
      journey.folders.map(folder => ({
        label: folder.title ?? intl.formatMessage({ id: `models.folderKind.${folder.kind}` }),
        value: folder.id
      }))
    );

    folders.unshift({
      label: intl.formatMessage({ id: 'models.folderKind.myFiles' }),
      value: ''
    });
    return folders;
  }, [intl, journey.folders]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    return updateWorksheet({
      variables: {
        ...form.getValues(),
        id: worksheet.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.worksheetUpdate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.worksheet'
      });
    });
  }, [worksheet.id, form, intl, onSuccess, updateWorksheet]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData>
        autofocus={autofocus}
        control={form.control}
        error={form.formState.errors.title}
        label={intl.formatMessage({ id: 'forms.worksheet.titleLabel' })}
        name="title"
        required
        spellcheck
      />
      {myExpert && (
        <RichTextArea<FormData>
          control={form.control}
          error={form.formState.errors.description}
          journeyId={null}
          label={intl.formatMessage({ id: 'forms.worksheet.descriptionLabel' })}
          name="description"
          required
          sharableURLSWithoutJourneyId
        />
      )}
      <div className={worksheet.fileResource ? '' : 'ion-hide'}>
        <Select<FormData>
          control={form.control}
          error={form.formState.errors.folderId}
          label={intl.formatMessage({
            id: 'forms.worksheet.folderIdLabel'
          })}
          name="folderId"
          options={folderOptions}
        />
      </div>
      <div className="ion-margin-top">
        <Checkbox<FormData> control={form.control} error={form.formState.errors.completed} name="completed">
          <FormattedMessage
            id={`models.worksheet.${completed ? 'markAsIncomplete' : 'markAsComplete'}`}
            values={{ expertFirstName: journey.primaryExpert?.firstName }}
          />
        </Checkbox>
      </div>

      <FormButtons hookForm={form} onCancel={onCancel} saveKey="dictionary.save" />
    </Form>
  );
};

export default WorksheetForm;
